import {
  CreateCartDataT,
  CartT,
  CreateLineItemDataT,
  CreateLineItemsDataT,
  UpdateLineItemDataT,
  UpdateLineItemsDataT,
  NoteT,
  CartLineT,
} from '@lambda/apis/src/cart/types';
import { Dispatch, SetStateAction } from 'react';
import settings from '@/settings';

export const fetchCartById = async (
  cartId: string,
  setCart?: Dispatch<SetStateAction<CartT | null>>,
): Promise<CartT | null> => {
  try {
    const response = await fetch(`/api/cart-v2/${cartId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    const data: CartT = await response.json();

    if (setCart) {
      setCart(data);
    }

    return data;
  } catch (error) {
    return null;
  }
};

export const updateCartLineItem = async (
  cartId: string,
  lineItemId: string,
  lineItem: UpdateLineItemDataT,
): Promise<CartT | null> => {
  try {
    const response = await fetch(
      `/api/cart-v2/${cartId}/lineItem/${lineItemId}/update`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(lineItem),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to update cart line item');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const deleteCartLineItem = async (
  cartId: string,
  lineItemId: string,
): Promise<CartT | null> => {
  try {
    const response = await fetch(
      `/api/cart-v2/${cartId}/lineItem/${lineItemId}/delete`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error('Failed to delete cart line item');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const deleteCartLineItems = async (
  cartId: string,
  lineIds: string[],
): Promise<CartT | null> => {
  try {
    const response = await fetch(`/api/cart-v2/${cartId}/lineItem/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lineIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to delete cart line items');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const createCartLineItems = async (
  cartId: string,
  lines: CreateLineItemsDataT['lines'],
): Promise<CartT | null> => {
  try {
    const response = await fetch(`/api/cart-v2/${cartId}/lineItem/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lines }),
    });

    if (!response.ok) {
      throw new Error('Failed to add cart line items');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const updateCartLineItems = async (
  cartId: string,
  lines: UpdateLineItemsDataT['lines'],
): Promise<CartT | null> => {
  try {
    const response = await fetch(`/api/cart-v2/${cartId}/lineItem/update`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lines }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart line items');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const createCart = async (
  cartData: CreateCartDataT,
): Promise<CartT | null> => {
  try {
    const response = await fetch('/api/cart-v2/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    });

    if (!response.ok) {
      throw new Error('Failed to create cart');
    }

    const data: CartT = await response.json();

    return data;
  } catch (error) {
    return null;
  }
};

export const createCartLineItem = async (
  cartId: string,
  lineItem: CreateLineItemDataT,
  setCart?: Dispatch<SetStateAction<CartT | null>>,
): Promise<CartT | null> => {
  try {
    const response = await fetch(`/api/cart-v2/${cartId}/lineItem/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(lineItem),
    });

    if (!response.ok) {
      throw new Error('Failed to create cart line item');
    }

    const data: CartT = await response.json();

    if (setCart) {
      setCart(data);
    }

    return data;
  } catch (error) {
    return null;
  }
};

export const encodeDecodeForNote = {
  encode: ({ sku, title }: { sku: string; title: string }) => [
    {
      name: 'For',
      value: `[${sku}] ${title}`,
    },
  ],
  decode: (notes: NoteT[]) =>
    /^\[(.*)\] /.exec(notes.find((a) => a.name === 'For')?.value || '')?.[1],
};

export const generateIdNote = (vendor: string, sku: string) => {
  // MsCatalog2 Vendor uses store#vendor
  if (vendor.startsWith('reebelo-')) {
    return [
      {
        name: 'ID',
        value: `${vendor}#${sku}`,
      },
    ];
  }

  // ES Offers Vendor is just vendor
  return [
    {
      name: 'ID',
      value: `${settings.store}#${vendor}#${sku}`,
    },
  ];
};

export const generateAddCartDateTimeNote = (lineItem?: CartLineT) => {
  const value = new Date().toISOString();
  const existingValue =
    lineItem?.notes?.find((note) => note.name === '_add_cart_date_time')
      ?.value ?? '';

  return [
    {
      name: '_add_cart_date_time',
      value: existingValue !== '' ? existingValue : value,
    },
  ];
};
